// Client for the bootstrap API (api/bootstrap.php) — combines several
// always-needed read-only endpoints into one HTTP round trip.
import type { MediaSlotRow } from './mediaApi';
import type { PageSeo } from './pageSeoApi';
import type { HeroSlide } from './heroSlidesApi';
import type { TrustStat } from './trustStatsApi';
import type { DistanceItem } from './distancesApi';
import type { GuestReview } from './reviewsApi';
import type { ReviewVideo } from './reviewVideosApi';
import type { ReviewPhoto } from './reviewPhotosApi';

const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export interface LayoutBootstrap {
  media: Record<string, MediaSlotRow>;
  pageSeo: Record<string, PageSeo>;
  siteSettings: Record<string, string>;
  customPageSlugs: string[];
  blogSlugs: string[];
}

export interface HomeBootstrap {
  heroSlides: HeroSlide[];
  trustStats: TrustStat[];
  distances: DistanceItem[];
}

export interface ReviewsBootstrap {
  reviews: GuestReview[];
  trustStats: TrustStat[];
  videos: ReviewVideo[];
  photos: ReviewPhoto[];
}

export async function fetchLayoutBootstrap(): Promise<LayoutBootstrap> {
  const res = await fetch(`${API_BASE}/bootstrap.php?action=layout`);
  const data = await res.json();
  if (!data.success) throw new Error(data.message ?? 'Failed to load site data');

  const media: Record<string, MediaSlotRow> = {};
  (data.media as MediaSlotRow[]).forEach((s) => {
    media[s.slot_key] = s;
  });
  const pageSeo: Record<string, PageSeo> = {};
  (data.pageSeo as PageSeo[]).forEach((p) => {
    pageSeo[p.page_key] = p;
  });

  return {
    media,
    pageSeo,
    siteSettings: data.siteSettings,
    customPageSlugs: data.customPageSlugs ?? [],
    blogSlugs: data.blogSlugs ?? [],
  };
}

export async function fetchHomeBootstrap(): Promise<HomeBootstrap> {
  const res = await fetch(`${API_BASE}/bootstrap.php?action=home`);
  const data = await res.json();
  if (!data.success) throw new Error(data.message ?? 'Failed to load home page data');
  return { heroSlides: data.heroSlides, trustStats: data.trustStats, distances: data.distances };
}

export async function fetchReviewsBootstrap(): Promise<ReviewsBootstrap> {
  const res = await fetch(`${API_BASE}/bootstrap.php?action=reviews`);
  const data = await res.json();
  if (!data.success) throw new Error(data.message ?? 'Failed to load reviews page data');
  return { reviews: data.reviews, trustStats: data.trustStats, videos: data.videos, photos: data.photos };
}
